import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { all, call, select, take, takeEvery } from 'redux-saga/effects';
import cloneDeep from 'lodash/cloneDeep';
import {
  AddVertragToAngebote,
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
  GlobalPut,
  ModalType,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  areAppTypesEquivalent,
  FEVorvertragsnummer,
  getRisikoortIfAllHaveElementarAndHaveSameRisikoort,
  StandaloneProdukt,
  VnWechselAngaben,
  Vorbelegungdaten,
  Vorvertrag,
  VwgVorbelegungdaten,
  normalizeApp,
} from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import dayjs from 'dayjs';
import { IAppState } from '../appState';
import {
  isEHVVertrag,
  isStandaloneVertrag,
  selectAlleVorvertraege,
  selectHasRsAndRsVermietungVorvertraege,
  selectHasRsVermietungVorvertragMitBeibehaltung,
  selectSelectedWeiterenVertrag,
  selectVorvertraege,
} from '../selectors/vorvertragsUebersicht.selector';

export function* multiVertragSaga(): SagaIterator {
  yield takeEvery(
    GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE_REQUESTED,
    safeSaga(addOtherVorvertraegeWithSameType, App.SHOP_DASHBOARD),
  );
}

export function* addOtherVorvertraegeWithSameType(action: GlobalAction<AddVertragToAngebote>): SagaIterator {
  const state: IAppState = yield select();
  const globalActions = new GlobalActions();
  const payload: AddVertragToAngebote = generatePayloadWithVorvertragsnummern(
    cloneDeep(action.payload),
    state,
  );

  if (
    areAppTypesEquivalent(payload.appId, App.VWG) &&
    payload.vorvertragsNummern &&
    payload.vorvertragsNummern.length > 0 &&
    !payload.beibehalten
  ) {
    const vorvertragToCheck = {
      vertragsnummer: payload.vorvertragForVorbelegung?.vertragsnummer,
      produktIds: [payload.appId],
      spLaufnummer: payload.vorvertragForVorbelegung?.spLaufnummer,
    } as Vorvertrag;
    if (!selectSelectedWeiterenVertrag(state, vorvertragToCheck)) {
      checkElementarInVorvertraegen(payload, state);
    }
    GlobalEventDispatcher.getInstance().dispatch(
      globalActions.requestVnWechselData(payload.vorvertragsNummern),
    );
    const vnWechselData: GlobalAction<VnWechselAngaben> = yield take(
      GlobalActions.VN_WECHSEL_DATA_PROVIDED,
    );
    payload.vnWechselAngaben = vnWechselData.payload;
  }

  if (payload.rsAndRsVermietung) {
    return yield* handleRsAndRsVermietung(action, payload, state);
  }

  const vorvertragsNummernZurUmstellung: FEVorvertragsnummer[] = [];
  payload.vorvertragsNummern?.forEach(vertrag => {
    const isVertragForVorbelegung =
      vertrag?.vertragsnummer === payload.vorvertragForVorbelegung?.vertragsnummer &&
      (vertrag?.spLaufnummer === payload.vorvertragForVorbelegung?.spLaufnummer ||
        (!vertrag?.spLaufnummer && !payload.vorvertragForVorbelegung?.spLaufnummer));
    if (!isVertragForVorbelegung) {
      vorvertragsNummernZurUmstellung.push(vertrag);
    }
  });

  if (
    vorvertragsNummernZurUmstellung.length > 0 &&
    !payload.noUmgestellteIdsHinweis &&
    !payload.beibehalten
  ) {
    GlobalEventDispatcher.getInstance().dispatch(
      globalActions.requestModal(ModalType.MULTIPLE_RS_VORVERTRAEGE, {
        body: getHinweisTextForModal(vorvertragsNummernZurUmstellung),
      }),
    );
  }

  return yield call(GlobalPut.getInstance().put, globalActions.addVertragToAngebote(payload));
}

function generatePayloadWithVorvertragsnummern(
  payload: AddVertragToAngebote,
  state: IAppState,
  isAdditionalPayload = false,
): AddVertragToAngebote {
  const multipleVorvertraegeApps: App[] = [
    App.RS,
    App.RS_VERMIETUNG,
    App.PH,
    App.THH_HUND,
    App.THH_PFERD,
    App.JHP,
  ];
  if (!payload.beibehalten) {
    payload.angebotsdatum =
      state.transientState?.customAngebotsdatum ?? dayjs().format(DATEFORMAT_SHORT_ISO);
  }
  if (multipleVorvertraegeApps.includes(payload.appId)) {
    const alleVorvertraege = selectAlleVorvertraege(state, true);
    const appVorvertraege = getAllOtherVorvertraegeForApp(payload, state);
    if (!hasFirmenvorvertrag(payload.vorvertragsNummern, alleVorvertraege)) {
      payload.vorvertragsNummern = payload.vorvertragsNummern.concat(
        appVorvertraege.map(multiVertragUpdateData => {
          return {
            vertragsnummer: multiVertragUpdateData.vertragsnummer,
            spLaufnummer: multiVertragUpdateData.spLaufnummer,
            produktIds: multiVertragUpdateData.produktIds,
          };
        }),
      );
    }

    if (payload.rsAndRsVermietung) {
      const isRsVermietungAndHasBeibehaltung =
        payload.appId === App.RS_VERMIETUNG &&
        selectHasRsVermietungVorvertragMitBeibehaltung(state);

      if (
        !isAdditionalPayload &&
        !isRsVermietungAndHasBeibehaltung &&
        payload.rsAndRsVermietung.hasFirmen &&
        !payload.rsAndRsVermietung.shouldCombine
      ) {
        payload.vorvertragsNummern = payload.vorvertragsNummern.filter(
          feVorvertragsnummer =>
            !isRsVermietungVertrag(
              feVorvertragsnummer.vertragsnummer,
              feVorvertragsnummer.spLaufnummer,
              alleVorvertraege,
            ),
        );
      } else if (
        isAdditionalPayload &&
        payload.rsAndRsVermietung.hasFirmen &&
        !payload.rsAndRsVermietung.shouldCombine
      ) {
        payload.vorvertragsNummern = payload.vorvertragsNummern.filter(feVorvertragsnummer =>
          isRsVermietungVertrag(
            feVorvertragsnummer.vertragsnummer,
            feVorvertragsnummer.spLaufnummer,
            alleVorvertraege,
          ),
        );
      }
    }
    {
      const firstVorvertrag = payload.vorvertragsNummern?.[0];
      const sourceAppType = firstVorvertrag?.produktIds?.[0] ?? payload.appId;

      payload.appId = normalizeApp(payload.appId, {
        sourceAppType: sourceAppType,
        beibehalten: payload.beibehalten,
        featureToggles: state.transientState?.featureToggles as { [key: string]: boolean },
      });
    }

    payload.vorvertragSofortschutz =
      appVorvertraege.some(
        multiVertragUpdateData => multiVertragUpdateData.sofortschutz === true,
      ) || payload.vorvertragSofortschutz;
  }

  payload.vorvertragsNummern = payload.vorvertragsNummern?.map(feVorvertragsnummer => {
    return {
      ...feVorvertragsnummer,
      produktIds: feVorvertragsnummer.produktIds ? feVorvertragsnummer.produktIds : [payload.appId],
    };
  });

  return payload;
}

function* addSeparateRsAndRsVermietungVertraege(
  action: GlobalAction<AddVertragToAngebote>,
  payload: AddVertragToAngebote,
  state: IAppState,
): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const globalActions = new GlobalActions();
  const isRsVermietungWithFirmenRs =
    payload.appId === App.RS_VERMIETUNG && payload.rsAndRsVermietung.hasFirmen;
  const isRsVermietungWithouthoutFirmenRs =
    payload.appId === App.RS_VERMIETUNG && !payload.rsAndRsVermietung.hasFirmen;
  let additionalPayload: AddVertragToAngebote = {
    ...payload,
    appId: isRsVermietungWithouthoutFirmenRs ? App.RS : App.RS_VERMIETUNG,
    vorvertragsNummern: isRsVermietungWithFirmenRs
      ? cloneDeep(action.payload.vorvertragsNummern)
      : [],
  };
  payload.appId = isRsVermietungWithouthoutFirmenRs ? App.RS_VERMIETUNG : App.RS;
  additionalPayload = generatePayloadWithVorvertragsnummern(additionalPayload, state, true);

  return yield all(
    [payload, additionalPayload].map(payload =>
      call(globalPut.put, globalActions.addVertragToAngebote(payload)),
    ),
  );
}

function* handleRsAndRsVermietung(
  action: GlobalAction<AddVertragToAngebote>,
  payload: AddVertragToAngebote,
  state: IAppState,
): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const globalActions = new GlobalActions();

  if (payload.rsAndRsVermietung.shouldCombine) {
    payload.appId = App.RS;
    return yield call(globalPut.put, globalActions.addVertragToAngebote(payload));
  } else if (!payload.rsAndRsVermietung.shouldCombine) {
    if (
      !selectHasRsAndRsVermietungVorvertraege(state, payload.appId) ||
      selectHasRsVermietungVorvertragMitBeibehaltung(state)
    ) {
      return yield call(globalPut.put, globalActions.addVertragToAngebote(payload));
    } else {
      return yield* addSeparateRsAndRsVermietungVertraege(action, payload, state);
    }
  }
}

function checkElementarInVorvertraegen(payload: AddVertragToAngebote, state: IAppState) {
  const vorbelegungdaten: Array<VwgVorbelegungdaten> = [];
  payload.vorvertragsNummern.forEach(value => {
    const dataFromVertrag = getVwgVorbelegungsdataFromVertrag(value, state.vorvertraege);
    if (dataFromVertrag) {
      vorbelegungdaten.push(dataFromVertrag);
    }
    const dataFromProdukt = getVwgVorbelegungsdataFromProdukt(value, state.standaloneProdukte);
    if (dataFromProdukt) {
      vorbelegungdaten.push(dataFromProdukt);
    }
  });
  payload.risikoortForElementar =
    getRisikoortIfAllHaveElementarAndHaveSameRisikoort(vorbelegungdaten);
}

function getVwgVorbelegungsdataFromVertrag(
  vorvertragsNummer: FEVorvertragsnummer,
  vertrag: Array<Vorvertrag>,
): VwgVorbelegungdaten {
  return getVwgVorbelegungsdaten(
    vertrag.find(
      vv =>
        vv.spLaufnummer === vorvertragsNummer.spLaufnummer &&
        vv.vertragsnummer === vorvertragsNummer.vertragsnummer &&
        vv.produktIds.some(value => areAppTypesEquivalent(value, App.VWG)),
    )?.vorbelegungdaten,
  );
}

function getVwgVorbelegungsdataFromProdukt(
  vorvertragsNummer: FEVorvertragsnummer,
  vertrag: Array<StandaloneProdukt>,
): VwgVorbelegungdaten {
  return getVwgVorbelegungsdaten(
    vertrag.find(
      produkt =>
        produkt.spLaufnummer === vorvertragsNummer.spLaufnummer &&
        produkt.vertragsnummer === vorvertragsNummer.vertragsnummer,
    )?.vorbelegungdaten,
  );
}

function getVwgVorbelegungsdaten(vorbelegungsdaten: Array<Vorbelegungdaten>): VwgVorbelegungdaten {
  return vorbelegungsdaten?.find(value => areAppTypesEquivalent(value.forApp, App.VWG)) as VwgVorbelegungdaten;
}

function hasFirmenvorvertrag(
  feVorvertragsnummern: FEVorvertragsnummer[],
  alleVorvertraege: Array<StandaloneProdukt | Vorvertrag>,
): boolean {
  for (const feVorvertragsnummer of feVorvertragsnummern) {
    const vorvertrag = alleVorvertraege.find(
      vorvertrag =>
        vorvertrag.vertragsnummer === feVorvertragsnummer.vertragsnummer &&
        (!feVorvertragsnummer.spLaufnummer ||
          vorvertrag.spLaufnummer === feVorvertragsnummer.spLaufnummer),
    );
    if (vorvertrag?.firmenvertrag) {
      return true;
    }
  }
  return false;
}

export function isRsVermietungVertrag(
  vorvertragsnummer: string,
  spLaufnummer: number,
  alleVorvertraege: Array<StandaloneProdukt | Vorvertrag>,
): boolean {
  const vorvertrag = alleVorvertraege.find(
    vorvertrag =>
      vorvertrag.vertragsnummer === vorvertragsnummer && vorvertrag.spLaufnummer === spLaufnummer,
  ) as Vorvertrag;
  return !!vorvertrag?.produktIds?.includes(App.RS_VERMIETUNG);
}

export function getAllOtherVorvertraegeForApp(
  payload: AddVertragToAngebote,
  state: IAppState,
): MultiVertragUpdateData[] {
  let alleVorvertraege: Vorvertrag[] = state.fremdVertraege
    .filter(fremdVertrag => !isStandaloneVertrag(fremdVertrag))
    .concat(selectVorvertraege(state, true)) as Vorvertrag[];
  const hasRsFirmenvorvertrag =
    payload.appId === App.RS && hasFirmenvorvertrag(payload.vorvertragsNummern, alleVorvertraege);

  if (!hasRsFirmenvorvertrag) {
    alleVorvertraege = alleVorvertraege.filter(vertrag => !vertrag.firmenvertrag);
  }

  let multiVertragUpdateData: MultiVertragUpdateData[] = mapVorvertraegeToMultiVertragUpdateData(
    alleVorvertraege,
    payload.vorvertragsNummern,
    payload.appId,
  );

  if (
    payload.rsAndRsVermietung?.shouldCombine &&
    selectHasRsAndRsVermietungVorvertraege(state, payload.appId)
  ) {
    const additionalMultiVertragUpdateData: MultiVertragUpdateData[] =
      payload.appId === App.RS
        ? mapVorvertraegeToMultiVertragUpdateData(
            alleVorvertraege,
            payload.vorvertragsNummern,
            App.RS_VERMIETUNG,
            payload.rsAndRsVermietung.shouldCombine,
          )
        : mapVorvertraegeToMultiVertragUpdateData(
            alleVorvertraege,
            payload.vorvertragsNummern,
            App.RS,
            payload.rsAndRsVermietung.shouldCombine,
          );
    multiVertragUpdateData = multiVertragUpdateData.concat(additionalMultiVertragUpdateData);
  }

  return multiVertragUpdateData;
}

function mapVorvertraegeToMultiVertragUpdateData(
  vorvertraege: Vorvertrag[],
  vorvertragsNummern: FEVorvertragsnummer[],
  appId: App,
  shouldCombineRsAndRsVermietung?: boolean,
): MultiVertragUpdateData[] {
  return vorvertraege
    .filter(
      vorvertrag =>
        vorvertrag.produktIds.includes(appId) &&
        vorvertrag.ersetzen &&
        !vorvertrag.isStorniert,
    )
    .filter(
      vorvertrag =>
        !vorvertragsNummern.some(
          value =>
            value.vertragsnummer === vorvertrag.vertragsnummer &&
            value.spLaufnummer === vorvertrag.spLaufnummer,
        ),
    )
    .filter(vorvertrag => !isEHVVertrag(vorvertrag))
    .map(vorvertrag => {
      return {
        vertragsnummer: vorvertrag.vertragsnummer,
        spLaufnummer: vorvertrag.spLaufnummer,
        sofortschutz: vorvertrag.vorvertragSofortschutz,
        firmenvertrag: vorvertrag.firmenvertrag,
        produktIds: [appId],
        shouldCombineRsAndRsVermietung,
      };
    });
}

export function getHinweisTextForModal(rsVorvertragsNummern: FEVorvertragsnummer[]) {
  return rsVorvertragsNummern
    .map(value => value.vertragsnummer)
    .filter(v => v != null)
    .join(', ');
}

export interface MultiVertragUpdateData extends FEVorvertragsnummer {
  sofortschutz: boolean;
  shouldCombineRsAndRsVermietung?: boolean;
}
